import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  Mail, 
  Phone, 
  Calendar, 
  Utensils,
  Sparkles,
  Rocket,
  Star,
  Shield,
  Zap,
  Clock,
  Award,
  Play,
  ArrowRight
} from 'lucide-react';

const ConfirmationPage = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [confetti, setConfetti] = useState(true);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Remove confetti após 3 segundos
    const timer = setTimeout(() => setConfetti(false), 3000);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timer);
    };
  }, []);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const nextSteps = [
    {
      number: 1,
      title: "Configure sua escola",
      description: "Adicione logo, informações e configurações básicas",
      icon: Shield,
      color: "from-blue-500 to-cyan-500"
    },
    {
      number: 2,
      title: "Cadastre produtos",
      description: "Adicione os produtos da sua cantina",
      icon: Zap,
      color: "from-purple-500 to-pink-500"
    },
    {
      number: 3,
      title: "Cadastre alunos",
      description: "Importe ou cadastre os alunos da escola",
      icon: Award,
      color: "from-green-500 to-emerald-500"
    },
    {
      number: 4,
      title: "Comece a vender",
      description: "Use o PDV para registrar as primeiras vendas",
      icon: Rocket,
      color: "from-orange-500 to-red-500"
    }
  ];

  const supportOptions = [
    {
      icon: Mail,
      title: "E-mail de suporte",
      value: "suporte@cantinatech.com.br",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Phone,
      title: "WhatsApp",
      value: "(11) 99999-9999",
      color: "from-green-500 to-green-600"
    },
    {
      icon: Calendar,
      title: "Treinamento gratuito",
      value: "Agende uma sessão de onboarding",
      color: "from-purple-500 to-purple-600"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Cursor personalizado */}
      <div 
        className="fixed w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full pointer-events-none z-50 mix-blend-difference transition-transform duration-100 ease-out"
        style={{
          left: mousePosition.x - 12,
          top: mousePosition.y - 12,
        }}
      />

      {/* Confetti effect */}
      {confetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`
              }}
            >
              <div className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full opacity-60" />
            </div>
          ))}
        </div>
      )}

      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }} />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full">
          {/* Success Header */}
          <div className="text-center mb-12">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center animate-pulse">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-bounce flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                🎉 Escola Cadastrada
              </span>
              <br />
              <span className="text-white">
                com Sucesso!
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-6 leading-relaxed">
              Sua conta foi criada e está <strong className="text-white">pronta para uso</strong>.<br />
              Agora você pode começar a usar todos os recursos do CantinaTech.
            </p>

            {/* Stats badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-full px-4 py-2">
                <span className="text-green-300 text-sm font-medium">✅ 30 dias grátis ativados</span>
              </div>
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full px-4 py-2">
                <span className="text-blue-300 text-sm font-medium">🚀 Setup em 24h</span>
              </div>
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-full px-4 py-2">
                <span className="text-purple-300 text-sm font-medium">🛡️ Suporte 24/7</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Next Steps */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Rocket className="w-6 h-6 mr-3 text-blue-400" />
                Próximos passos
              </h2>
              
              <div className="space-y-6">
                {nextSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex items-start space-x-4 group"
                      style={{ animationDelay: `${index * 200}ms` }}
                    >
                      <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-r ${step.color} rounded-xl flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300`}>
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Support Information */}
            <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-green-400" />
                Precisa de ajuda?
              </h2>
              
              <div className="space-y-6">
                {supportOptions.map((option, index) => {
                  const Icon = option.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex items-center space-x-4 p-4 bg-gray-800/50 rounded-xl border border-gray-600/30 hover:border-blue-500/50 transition-all duration-300 group cursor-pointer"
                    >
                      <div className={`w-12 h-12 bg-gradient-to-r ${option.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white text-sm mb-1">
                          {option.title}
                        </h3>
                        <p className="text-blue-400 text-sm font-medium">
                          {option.value}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick action buttons */}
              <div className="mt-8 space-y-3">
                <button className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/25 flex items-center justify-center group">
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Agendar Treinamento
                </button>
                
                <button className="w-full bg-gradient-to-r from-gray-700 to-gray-600 border border-gray-500 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:border-blue-500/50 flex items-center justify-center group">
                  <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Ver Tutoriais
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center space-y-6">
            <button
              onClick={handleContinue}
              className="group relative bg-gradient-to-r from-blue-600 to-purple-600 text-white px-12 py-4 rounded-2xl text-xl font-bold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 flex items-center justify-center mx-auto"
            >
              <Utensils className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
              Acessar Dashboard
              <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10" />
            </button>

            {/* Success metrics */}
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-2xl border border-gray-600/30 backdrop-blur-sm p-6 max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center justify-center">
                <Star className="w-5 h-5 mr-2 text-yellow-400" />
                Você está entre as melhores escolas!
              </h3>
              
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">+60%</div>
                  <div className="text-xs text-gray-400">Aumento médio nas vendas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">-85%</div>
                  <div className="text-xs text-gray-400">Redução no tempo de fila</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">98%</div>
                  <div className="text-xs text-gray-400">Satisfação dos pais</div>
                </div>
              </div>
            </div>

            {/* Email confirmation notice */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4 max-w-md mx-auto">
              <div className="flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400 mr-2" />
                <p className="text-sm text-blue-300">
                  Um e-mail de confirmação foi enviado com mais informações
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;